<script setup>
// Власний чат ефіру: стрічка + поле написання.
//
// Той самий рівень, що й ChatBody (гілки коментарів), — тільки
// всередині не чужий віджет, а наше вебсокет-зʼєднання: повідомлення
// приходять миттєво, медіа ховається під спойлер, а модератор прибирає
// зайве прямо зі стрічки (див. backend/src/chat).

const props = defineProps({
  // Кімната чату: приходить з адмінки разом із рештою налаштувань ефіру
  room: { type: String, required: true },
  // Ключ джерела для OBS: тільки на сторінці оверлея, у звичайного
  // глядача його немає
  overlayKey: { type: String, default: '' }
})

// «/» у полі написання просить розгорнути чат, якщо він згорнутий —
// саму колонку тримає live/Chat.vue, тож просто передаємо далі
const emit = defineEmits(['open-chat'])

const {
  messages,
  connected,
  me,
  online,
  muted,
  mutedUntil,
  denied,
  readonly,
  notice,
  limits,
  chatters,
  rejected,
  poll,
  canModerate,
  sendMessage,
  removeMessage,
  muteUser,
  reactMessage,
  uploadMedia,
  refreshProfile,
  createPoll,
  votePoll,
  endPoll,
  dropPoll
} = useLiveChat(() => props.room, { overlayKey: () => props.overlayKey })

// Два екрани поверх стрічки: свій профіль у чаті і форма опитування.
// Обидва перекривають саме стрічку, бо колонка чату вузька, а читати чат
// і заповнювати форму одночасно однаково нікому не треба
const settingsOpen = ref(false)
const pollFormOpen = ref(false)
// Панель стрімера: статистика ефіру й швидкі налаштування — тільки глядачеві
// з галочкою «Стрімер» (сервер однаково не пустить інших)
const streamerOpen = ref(false)
const isStreamer = computed(() => !!me.value?.streamer && !readonly.value)

const openSettings = () => {
  pollFormOpen.value = false
  streamerOpen.value = false
  settingsOpen.value = true
}

const openPollForm = () => {
  settingsOpen.value = false
  streamerOpen.value = false
  pollFormOpen.value = true
}

const toggleStreamer = () => {
  settingsOpen.value = false
  pollFormOpen.value = false
  streamerOpen.value = !streamerOpen.value
}

/**
 * Профіль змінився з екрана налаштувань. Оновлюємо його і в себе, і на
 * зʼєднанні: сам сокет перечитує профіль не частіше ніж раз на 15 секунд,
 * а перше ж повідомлення після зміни має піти вже новим іменем
 */
const onProfileSaved = (profile) => {
  me.value = { ...me.value, ...profile }
  refreshProfile()
}

const onPollCreate = (data) => {
  if (!createPoll(data)) notice.value = 'Немає звʼязку з чатом — спробуйте ще раз'
}

// Голосувати може лише залогінений глядач: голоси рахуються по людях, а не
// по відкритих вкладках (джерело для OBS сюди теж не потрапляє)
const canVote = computed(() => !!me.value && !readonly.value)

// На що відповідаємо. Живе тут, а не в полі написання: вибирають його в
// стрічці, а показує смужку композер
const replyTo = ref(null)
const composer = ref(null)

const startReply = (message) => {
  replyTo.value = {
    id: message.id,
    author: message.author,
    text: message.text || '',
    media: Array.isArray(message.media) ? message.media.length > 0 : !!message.media,
    deleted: !!message.deleted
  }
}

const cancelReply = () => {
  replyTo.value = null
}

// Клік по ніку в стрічці дописує «@нік» у поле — звичний спосіб звернутись
const onMention = (name) => composer.value?.mention(name)

// Відповідати може той, кому взагалі є чим писати: у джерела OBS і в
// незалогіненого поля написання немає
const canReply = computed(() => !!me.value && !readonly.value)

// Чат закритий підпискою — бекенд не дав ні історії, ні кімнати. Малюємо
// той самий екран входу, що й гейт самого ефіру: причина в нас уже є,
// лишається перекласти її у звичний для сторінки вигляд
const gateAccess = computed(() =>
  denied.value
    ? {
        needsTwitch: !!denied.value.needsTwitch,
        needsMono: !!denied.value.needsMono,
        authenticated: !!denied.value.authenticated,
        hasTwitchSub: !!denied.value.hasTwitchSub,
        hasMono: !!denied.value.hasMono,
        allowed: false
      }
    : null
)

const listRef = ref(null)

// Якщо людина відгорнула історію вгору — не смикаємо її новим
// повідомленням; якщо внизу — тримаємось низу, навіть коли картинка в
// останньому повідомленні доїхала пізніше за нього (див. useStickyScroll)
const {
  stuck: stickToBottom,
  missed,
  scrollToBottom,
  onScroll,
  onAppend
} = useStickyScroll(listRef)

watch(messages, (next, prev) => {
  onAppend(Math.max(0, next.length - (prev?.length || 0)))
})

// Помилку («не так швидко», «завелике медіа») показуємо секунди три:
// вона про останню дію, і висіти над чатом їй нема чого
let noticeTimer = null

watch(notice, (value) => {
  if (noticeTimer) clearTimeout(noticeTimer)
  if (!value) return
  noticeTimer = setTimeout(() => {
    notice.value = ''
  }, 4000)
})

onBeforeUnmount(() => {
  if (noticeTimer) clearTimeout(noticeTimer)
  if (flashTimer) clearTimeout(flashTimer)
})

const onMute = ({ userId, seconds }) => muteUser(userId, seconds)

// Перехід до цитованого повідомлення. Воно може бути вже за межами
// стрічки (тримаємо останні 300) — тоді просто нічого не робимо: тягнути
// заради одного рядка окремий шматок історії не варто
const flashed = ref(null)
let flashTimer = null

const jumpTo = (id) => {
  const el = listRef.value?.querySelector(`#chat-msg-${CSS.escape(String(id))}`)
  if (!el) {
    notice.value = 'Того повідомлення вже немає в стрічці'
    return
  }

  el.scrollIntoView({ block: 'center', behavior: 'smooth' })

  // Підсвічуємо на пару секунд: без цього в щільній стрічці незрозуміло,
  // куди саме нас перекинуло
  flashed.value = id
  if (flashTimer) clearTimeout(flashTimer)
  flashTimer = setTimeout(() => {
    flashed.value = null
  }, 2000)
}
</script>

<template>
  <div class="relative flex flex-col flex-1 bg-base-300 min-h-0">
    <!-- Смужка стану: скільки в чаті і чи ми взагалі підключені -->
    <div
      class="flex flex-shrink-0 items-center gap-2 bg-base-200/60 px-2 py-1 text-[10px] text-base-content/50"
    >
      <span
        class="inline-block rounded-full w-1.5 h-1.5"
        :class="connected ? 'bg-success' : 'bg-warning animate-pulse'"
      />
      <span>{{ connected || denied ? 'Чат ефіру' : 'Підключаємось…' }}</span>

      <span
        v-if="!denied"
        class="ml-auto"
        >{{ online }} у чаті</span
      >

      <span
        v-if="canModerate"
        class="bg-primary/20 px-1.5 rounded text-primary"
        >модератор</span
      >

      <!-- Опитування — там же, де решта модерації: окремої панелі під нього
           заводити нема для чого -->
      <button
        v-if="canModerate && !denied"
        class="text-base-content/50 hover:text-primary"
        title="Створити опитування"
        @click="openPollForm"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <line
            x1="6"
            y1="20"
            x2="6"
            y2="12"
          />
          <line
            x1="12"
            y1="20"
            x2="12"
            y2="4"
          />
          <line
            x1="18"
            y1="20"
            x2="18"
            y2="14"
          />
        </svg>
      </button>

      <!-- Панель стрімера: глядачі, темп чату, назва, доступ, пресети -->
      <button
        v-if="isStreamer"
        class="hover:text-primary"
        :class="streamerOpen ? 'text-primary' : 'text-base-content/50'"
        title="Панель стрімера"
        @click="toggleStreamer"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <rect
            x="2"
            y="7"
            width="20"
            height="14"
            rx="2"
          />
          <path d="m17 2-5 5-5-5" />
        </svg>
      </button>

      <!-- Свій нік, колір і значок. Кнопка вгорі, а не в полі написання:
           це налаштування чату, а не частина повідомлення -->
      <button
        v-if="me && !readonly"
        class="text-base-content/50 hover:text-primary"
        title="Мій профіль у чаті"
        @click="openSettings"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <circle
            cx="12"
            cy="12"
            r="3"
          />
          <path
            d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z"
          />
        </svg>
      </button>
    </div>

    <!-- Опитування висить між смужкою стану і стрічкою: у чаті, що біжить,
         рядок із питанням поїхав би вгору рівно тоді, коли по ньому
         починають клікати -->
    <ChatLivePoll
      v-if="poll && !denied"
      :poll="poll"
      :can-vote="canVote"
      :can-moderate="canModerate"
      @vote="votePoll"
      @end="endPoll"
      @drop="dropPoll"
    />

    <!--
      Чат закритий підпискою: ані стрічки, ані поля написання — бекенд
      сюди нічого й не прислав. Показуємо ті самі кнопки входу, що й гейт
      самого ефіру, щоб не пояснювати вдруге, чого бракує
    -->
    <div
      v-if="denied"
      class="flex flex-col justify-center items-center gap-3 p-4 text-center grow"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="28"
        height="28"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.5"
        class="opacity-40"
      >
        <rect
          x="3"
          y="11"
          width="18"
          height="11"
          rx="2"
        />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </svg>

      <p class="text-sm text-base-content/70">
        {{
          denied.needsTwitch && denied.needsMono
            ? 'Чат ефіру — для сабів Twitch і Монобази'
            : denied.needsMono
              ? 'Чат ефіру — для Монобази'
              : 'Чат ефіру — для сабів Twitch'
        }}
      </p>

      <LiveLoginButtons :access="gateAccess" />
    </div>

    <div
      v-else
      ref="listRef"
      class="flex flex-col gap-0.5 p-1.5 min-h-0 overflow-y-auto grow"
      @scroll.passive="onScroll"
    >
      <p
        v-if="!messages.length"
        class="my-auto text-center text-base-content/50 text-xs"
      >
        {{ connected ? 'Повідомлень ще немає — напишіть перше' : 'Завантажуємо чат…' }}
      </p>

      <ChatLiveMessage
        v-for="message in messages"
        :key="message.id"
        :message="message"
        :can-moderate="canModerate"
        :can-reply="canReply"
        :me-id="me?.id || null"
        :me-name="me?.name || ''"
        :flash="flashed === message.id"
        @delete="removeMessage"
        @mute="onMute"
        @react="reactMessage"
        @reply="startReply"
        @mention="onMention"
        @jump="jumpTo"
      />
    </div>

    <!-- Відгорнули вгору — показуємо, скільки пропустили -->
    <button
      v-if="!stickToBottom && !denied"
      class="bottom-[64px] left-1/2 z-20 absolute bg-primary/90 hover:bg-primary shadow px-3 py-1 rounded-full text-[11px] text-primary-content -translate-x-1/2"
      @click="scrollToBottom"
    >
      {{ missed ? `Нових повідомлень: ${missed}` : 'Вниз' }}
    </button>

    <p
      v-if="notice"
      class="flex-shrink-0 bg-error/15 px-2 py-1 text-[11px] text-error text-center"
    >
      {{ notice }}
    </p>

    <!-- readonly — це джерело для OBS: воно зайшло ключем, і писати ним
         нікому -->
    <ChatLiveComposer
      v-if="!denied && !readonly"
      ref="composer"
      :send="sendMessage"
      :upload="uploadMedia"
      :authenticated="!!me"
      :muted="muted"
      :muted-until="mutedUntil"
      :connected="connected"
      :max-length="limits.maxText"
      :chatters="chatters"
      :me-name="me?.name || ''"
      :reply-to="replyTo"
      :rejected="rejected"
      @open-chat="emit('open-chat')"
      @cancel-reply="cancelReply"
    />

    <ChatLiveSettings
      v-if="settingsOpen && me"
      :me="me"
      @saved="onProfileSaved"
      @close="settingsOpen = false"
    />

    <ChatStreamerPanel
      v-if="streamerOpen && isStreamer"
      @close="streamerOpen = false"
    />

    <ChatLivePollForm
      v-if="pollFormOpen && canModerate"
      @create="onPollCreate"
      @close="pollFormOpen = false"
    />
  </div>
</template>
